import {
  FunctionIcon,
  MagnifyingGlassIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import { cn } from '@/lib/utils';
import { WorkbookItemIcon } from './workbookItemIcon';
import type {
  FormulaField,
  FormulaFieldOrigin,
  FormulaTable,
} from './worksheetFormulaFields';

/** What a Reference chip shows: the Field, and where it comes from. */
function useChipText(name: string, field: FormulaField | undefined) {
  const gt = useGT();
  if (!field)
    return {
      origin: null,
      label: gt('Unknown column {name}', {
        context: 'worksheet formula reference chip',
        name,
      }),
    };
  const { origin } = field;
  if (origin.kind === 'input')
    return {
      origin: origin.inputName,
      label: gt('{name} from {source}', {
        context: 'worksheet formula reference chip',
        name,
        source: origin.inputName,
      }),
    };
  if (origin.kind === 'lookup')
    return {
      origin: origin.sourceName,
      label: gt('{name}, looked up from {source}', {
        context: 'worksheet formula reference chip',
        name,
        source: origin.sourceName,
      }),
    };
  return {
    origin: null,
    label: gt('Calculated column {name}', {
      context: 'worksheet formula reference chip',
      name,
    }),
  };
}

/**
 * A Field's origin icon: its input's Item icon (Excel for a file Source), a
 * magnifier for a Lookup, a function sign for a calculated column.
 */
export function FormulaOriginIcon({
  origin,
  size,
}: {
  origin: FormulaFieldOrigin;
  size: number;
}) {
  if (origin.kind === 'input')
    return (
      <WorkbookItemIcon
        kind={origin.inputIcon}
        aria-hidden
        size={size}
        className="shrink-0"
      />
    );
  if (origin.kind === 'lookup' && origin.sourceIcon === 'xlsx')
    return (
      <WorkbookItemIcon
        kind="xlsx"
        aria-hidden
        size={size}
        className="shrink-0"
      />
    );
  const Icon = origin.kind === 'lookup' ? MagnifyingGlassIcon : FunctionIcon;
  return <Icon aria-hidden size={size} className="shrink-0" />;
}

/**
 * A Formula reference as a chip: an icon for its origin (input, Lookup,
 * calculated column), the origin's name, and the Field. A reference to no Field
 * renders as an error chip.
 */
export function FormulaReferenceChip({
  name,
  field,
}: {
  name: string;
  field: FormulaField | undefined;
}) {
  const { origin, label } = useChipText(name, field);
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      data-formula-reference={field?.origin.kind ?? 'unknown'}
      className={cn(
        'mx-px inline-flex max-w-full items-center gap-1 rounded border px-1 align-baseline font-sans text-xs leading-5 whitespace-nowrap select-none',
        !field && 'border-destructive/50 bg-destructive/10 text-destructive',
        field?.origin.kind === 'input' && 'border-border bg-muted',
        field?.origin.kind === 'lookup' &&
          'border-primary/30 bg-primary/5 text-foreground',
        field?.origin.kind === 'calculated' && 'border-border bg-background',
      )}
    >
      {field ? (
        <FormulaOriginIcon origin={field.origin} size={12} />
      ) : (
        <WarningCircleIcon aria-hidden size={12} className="shrink-0" />
      )}
      {origin !== null && (
        <span className="truncate text-muted-foreground">{origin} ·</span>
      )}
      <span className="truncate">{name}</span>
    </span>
  );
}

/** A Table reference chip's label, and whether its table lacks the column. */
function useTableChipLabel(
  table: FormulaTable | undefined,
  column: string | null,
) {
  const gt = useGT();
  if (!table)
    return {
      label: gt('Unknown table', {
        context: 'worksheet formula table reference chip',
      }),
      unknownColumn: false,
    };
  if (column === null)
    return {
      label: gt('Table {table}', {
        context: 'worksheet formula table reference chip',
        table: table.name,
      }),
      unknownColumn: false,
    };
  const unknownColumn =
    table.columns.length > 0 && !table.columns.includes(column);
  return {
    label: unknownColumn
      ? gt('Unknown column {name} in {table}', {
          context: 'worksheet formula table reference chip',
          name: column,
          table: table.name,
        })
      : gt('{name} from {table}', {
          context: 'worksheet formula table reference chip',
          name: column,
          table: table.name,
        }),
    unknownColumn,
  };
}

/**
 * A Table reference as a chip: the table's icon and name, and the column a
 * lookup reads (none for VLOOKUP's whole table). A removed table, or a column
 * the table doesn't have, renders as an error chip.
 */
export function FormulaTableReferenceChip({
  table,
  column,
}: {
  table: FormulaTable | undefined;
  column: string | null;
}) {
  const { label, unknownColumn } = useTableChipLabel(table, column);
  const invalid = !table || unknownColumn;
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      data-formula-reference={invalid ? 'unknown' : 'table'}
      className={cn(
        'mx-px inline-flex max-w-full items-center gap-1 rounded border px-1 align-baseline font-sans text-xs leading-5 whitespace-nowrap select-none',
        invalid
          ? 'border-destructive/50 bg-destructive/10 text-destructive'
          : 'border-primary/30 bg-primary/5 text-foreground',
      )}
    >
      {table && !unknownColumn ? (
        <WorkbookItemIcon
          kind={table.icon}
          aria-hidden
          size={12}
          className="shrink-0"
        />
      ) : (
        <WarningCircleIcon aria-hidden size={12} className="shrink-0" />
      )}
      {table && (
        <span
          className={cn('truncate', column !== null && 'text-muted-foreground')}
        >
          {column === null ? table.name : `${table.name} ·`}
        </span>
      )}
      {column !== null && <span className="truncate">{column}</span>}
    </span>
  );
}
